import { useState, useEffect } from 'react';
import { X, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@spark/ui';

interface DebugInfo {
  lastError?: {
    message: string;
    timestamp: Date;
    source?: string;
    details?: any;
  };
  environment: string;
  version: string;
}

export function DebugBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    environment: import.meta.env.MODE,
    version: '1.0.0'
  });

  useEffect(() => {
    // Only show in development/staging
    if (import.meta.env.MODE !== 'production') {
      setIsVisible(true);
    }

    // Listen for errors
    const handleError = (event: ErrorEvent) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: {
          message: event.message,
          timestamp: new Date(),
          source: event.filename,
          details: {
            line: event.lineno,
            column: event.colno,
            error: event.error?.stack
          }
        }
      }));
    };

    // Listen for unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: {
          message: event.reason?.message || String(event.reason),
          timestamp: new Date(),
          source: 'Promise Rejection',
          details: event.reason
        }
      }));
    };

    // Listen for custom debug events
    const handleDebugEvent = (event: CustomEvent) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: {
          message: event.detail.message,
          timestamp: new Date(),
          source: event.detail.source,
          details: event.detail
        }
      }));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('app:debug:error', handleDebugEvent as EventListener);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('app:debug:error', handleDebugEvent as EventListener);
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 max-w-md z-50">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg shadow-lg">
        <div className="p-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
              <span className="font-medium text-sm text-yellow-800 dark:text-yellow-200">
                Debug Mode ({debugInfo.environment})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-6 w-6 p-0"
              >
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsVisible(false)}
                className="h-6 w-6 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {debugInfo.lastError && (
            <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
              Last Error: {debugInfo.lastError.message}
              <div className="text-xs text-yellow-600 dark:text-yellow-400">
                {debugInfo.lastError.timestamp.toLocaleTimeString()}
              </div>
            </div>
          )}
        </div>

        {isExpanded && debugInfo.lastError && (
          <div className="border-t border-yellow-200 dark:border-yellow-800 p-3">
            <div className="space-y-2 text-xs">
              <div>
                <strong>Source:</strong> {debugInfo.lastError.source || 'Unknown'}
              </div>
              {debugInfo.lastError.details && (
                <div>
                  <strong>Details:</strong>
                  <pre className="mt-1 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(debugInfo.lastError.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper to dispatch debug events
export function debugError(message: string, source: string, details?: any) {
  window.dispatchEvent(new CustomEvent('app:debug:error', {
    detail: { message, source, details }
  }));
}
